import React, { useEffect, useRef, useState } from 'react';

import { FiltrosRequisicoes } from '../../../api/orcamentoCiliaApi';
// Reuso do CSS de filtros do Contac e do complemento do modal de Usuários (at-61): o rodapé de
// 3 botões e a mensagem de erro vivem lá. Nada de CSS novo.
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../components/modal/filtro-usuarios/ModalFiltroUsuarios.css';
import '../../../shared/ui/modal-close-button.css';

/** O que a tela guarda (strings simples; a lista do backend é montada no envio). */
export interface FiltrosDaTela {
  estado: string;
  de: string;
  ate: string;
  oficina: string;
  importadoPor: string;
  tipo: '' | 'INICIAL' | 'COMPLEMENTAR';
  perdaTotal: string;
  atendimento: '' | 'COM' | 'SEM';
  teste: '' | 'SO_TESTES' | 'OCULTAR';
}

export const FILTROS_REQUISICOES_PADRAO: FiltrosDaTela = {
  estado: '',
  de: '',
  ate: '',
  oficina: '',
  importadoPor: '',
  tipo: '',
  perdaTotal: '',
  atendimento: '',
  teste: '',
};

/** Traduz o que a tela guarda para os parâmetros da rota. */
export const paraParametros = (f: FiltrosDaTela): FiltrosRequisicoes => ({
  estados: f.estado ? [f.estado] : undefined,
  de: f.de || undefined,
  ate: f.ate || undefined,
  oficina: f.oficina.trim() || undefined,
  importadoPor: f.importadoPor.trim() || undefined,
  tipo: f.tipo || undefined,
  perdaTotal: f.perdaTotal ? [f.perdaTotal] : undefined,
  atendimento: f.atendimento || undefined,
  teste: f.teste || undefined,
});

/*
 * Rótulo neutro para `ENVIADO` (13/08/2026): o estado engloba "Requisição Criada" e "Sem
 * Requisição". Filtrar por "Requisição Criada" e receber linhas "Sem Requisição" faria o filtro
 * parecer quebrado.
 */
const ESTADOS = [
  { value: 'EM_CONFERENCIA', label: 'Em Conferência' },
  { value: 'EM_AJUSTE', label: 'Em Ajuste' },
  { value: 'ENVIADO', label: 'Conferência Concluída' },
  { value: 'REJEITADO', label: 'Requisição Reprovada' },
  { value: 'APROVADO', label: 'Requisição Aprovada' },
  { value: 'AGUARDANDO_AUTORIZACAO', label: 'Aguardando Autorização' },
  { value: 'AUTORIZACAO_NEGADA', label: 'Autorização Negada' },
];

const PERDA_TOTAL = [
  { value: 'PT_NAS_DUAS', label: 'Perda total nas duas cestas' },
  { value: 'PT_SO_NA_MAIOR', label: 'Perda total só na cesta maior' },
  { value: 'ABAIXO', label: 'Abaixo do limite' },
  { value: 'SEM_FIPE', label: 'Sem valor FIPE' },
  { value: 'SEM_PRECO', label: 'Sem preço online ainda' },
  { value: 'NAO_CALCULADO', label: 'Não calculada' },
];

interface Props {
  onClose: () => void;
  /** Filtros em vigor: o modal abre refletindo o que está aplicado (BUG-V43). */
  filtrosAplicados: FiltrosDaTela;
  onAplicar: (filtros: FiltrosDaTela) => void;
}

/**
 * Filtros da lista de Requisições (orcamento-cilia/at-18), no molde do modal de Usuários (at-61):
 * estado local, saída só em "Aplicar Filtros", e **montagem condicional** pela tela (sem prop
 * `isOpen`), para o "Cancelar" descartar o rascunho de verdade.
 */
const FiltroRequisicoesModal: React.FC<Props> = ({ onClose, filtrosAplicados, onAplicar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [filtros, setFiltros] = useState<FiltrosDaTela>(filtrosAplicados);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const clicouFora = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) onClose();
    };
    const tecla = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', clicouFora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', clicouFora);
      document.removeEventListener('keydown', tecla);
    };
  }, [onClose]);

  const alterar = <K extends keyof FiltrosDaTela>(campo: K, valor: FiltrosDaTela[K]) => {
    setErro(null);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const aplicar = () => {
    if (filtros.de && filtros.ate && filtros.de > filtros.ate) {
      setErro('A data final não pode ser anterior à data inicial.');
      return;
    }
    onAplicar(filtros);
    onClose();
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros de Requisições</h2>
              <p className="filtro-modal-subtitle">Os filtros se combinam entre si e com a busca</p>
            </div>
            <button className="close-button" onClick={onClose} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
            <div className="filtro-section">
              <h3 className="filtro-section-title">Situação</h3>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="filtro-req-estado">Estado</label>
                  <div className="select-wrapper">
                    <select id="filtro-req-estado" value={filtros.estado} onChange={(e) => alterar('estado', e.target.value)}>
                      <option value="">Todos os estados</option>
                      {ESTADOS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-tipo">Tipo</label>
                  <div className="select-wrapper">
                    <select
                      id="filtro-req-tipo"
                      value={filtros.tipo}
                      onChange={(e) => alterar('tipo', e.target.value as FiltrosDaTela['tipo'])}
                    >
                      <option value="">Inicial e complementos</option>
                      <option value="INICIAL">Só inicial</option>
                      <option value="COMPLEMENTAR">Só complementos</option>
                    </select>
                  </div>
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-pt">Perda total</label>
                  <div className="select-wrapper">
                    <select id="filtro-req-pt" value={filtros.perdaTotal} onChange={(e) => alterar('perdaTotal', e.target.value)}>
                      <option value="">Todas</option>
                      {PERDA_TOTAL.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-atendimento">Atendimento na Oficina</label>
                  <div className="select-wrapper">
                    <select
                      id="filtro-req-atendimento"
                      value={filtros.atendimento}
                      onChange={(e) => alterar('atendimento', e.target.value as FiltrosDaTela['atendimento'])}
                    >
                      <option value="">Com e sem atendimento</option>
                      <option value="COM">Com atendimento</option>
                      <option value="SEM">Sem atendimento na Oficina</option>
                    </select>
                  </div>
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-teste">Requisições de teste</label>
                  <div className="select-wrapper">
                    <select
                      id="filtro-req-teste"
                      value={filtros.teste}
                      onChange={(e) => alterar('teste', e.target.value as FiltrosDaTela['teste'])}
                    >
                      <option value="">Mostrar</option>
                      <option value="OCULTAR">Ocultar</option>
                      <option value="SO_TESTES">Só as de teste</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="filtro-section">
              <h3 className="filtro-section-title">Oficina e pessoa</h3>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="filtro-req-oficina">Oficina contém</label>
                  <input
                    id="filtro-req-oficina"
                    type="text"
                    className="premium-text-input"
                    maxLength={100}
                    value={filtros.oficina}
                    onChange={(e) => alterar('oficina', e.target.value)}
                  />
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-importado-por">Importado por contém</label>
                  <input
                    id="filtro-req-importado-por"
                    type="text"
                    className="premium-text-input"
                    maxLength={100}
                    value={filtros.importadoPor}
                    onChange={(e) => alterar('importadoPor', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="filtro-section">
              <div className="filtro-section-header">
                <h3 className="filtro-section-title">Data de importação</h3>
                {(filtros.de || filtros.ate) && (
                  <button
                    type="button"
                    className="filtro-reset-btn"
                    onClick={() => {
                      setErro(null);
                      setFiltros((atual) => ({ ...atual, de: '', ate: '' }));
                    }}
                  >
                    Redefinir
                  </button>
                )}
              </div>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="filtro-req-de">Importado de</label>
                  <input id="filtro-req-de" type="date" className="premium-text-input" value={filtros.de} onChange={(e) => alterar('de', e.target.value)} />
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-req-ate">Importado até</label>
                  <input id="filtro-req-ate" type="date" className="premium-text-input" value={filtros.ate} onChange={(e) => alterar('ate', e.target.value)} />
                </div>
              </div>
              {erro && <p className="filtro-erro">{erro}</p>}
            </div>
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary filtro-btn-limpar"
              onClick={() => {
                setErro(null);
                setFiltros(FILTROS_REQUISICOES_PADRAO);
              }}
            >
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltroRequisicoesModal;
